import React, { useState, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { ChevronLeft, ChevronRight, BookOpen, Users, Clock, MapPin } from 'lucide-react';

const LessonFlow = ({ events, onEventClick, onBack }) => {
  const [selectedClass, setSelectedClass] = useState(null);
  const [dateRange, setDateRange] = useState(7); // Default to 1 week

  // Extract unique classes from events
  const classes = useMemo(() => {
    const classSet = new Set();
    events.forEach(event => {
      if (event.title && event.title.includes(':')) {
        // Extract class from title like "Maths: Year 8: 8E/Ma1"
        const parts = event.title.split(':');
        if (parts.length >= 3) {
          const className = parts[2].trim();
          classSet.add(className);
        }
      }
    });
    return Array.from(classSet).sort();
  }, [events]);

  // Get events for selected class
  const getClassEvents = (className) => {
    return events
      .filter(event => 
        event.title && 
        event.title.includes(className) &&
        event.start_time &&
        event.date
      )
      .sort((a, b) => {
        // Sort by date first, then by time
        if (a.date !== b.date) {
          return new Date(a.date) - new Date(b.date);
        }
        return a.start_time.localeCompare(b.start_time);
      });
  };

  // Get date range for display (including previous 2 lessons)
  const getDateRangeEvents = (classEvents) => {
    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(today.getDate() + dateRange);
    
    // Get upcoming events
    const upcomingEvents = classEvents.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate >= today && eventDate <= endDate;
    });
    
    // Get previous 2 lessons
    const previousEvents = classEvents.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate < today;
    }).slice(-2); // Get last 2 previous lessons
    
    // Combine and sort all events
    const allEvents = [...previousEvents, ...upcomingEvents];
    return allEvents.sort((a, b) => {
      if (a.date !== b.date) {
        return new Date(a.date) - new Date(b.date);
      }
      return a.start_time.localeCompare(b.start_time);
    });
  };

  // Group events by date
  const groupEventsByDate = (events) => {
    const grouped = {};
    events.forEach(event => {
      const date = event.date;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(event);
    });
    return grouped;
  };

  const classEvents = selectedClass ? getClassEvents(selectedClass) : [];
  const upcomingEvents = getDateRangeEvents(classEvents);
  const groupedEvents = groupEventsByDate(upcomingEvents);

  return (
    <div className="lesson-flow-container">
      <div className="lesson-flow-header">
        <button 
          className="back-button"
          onClick={onBack}
          aria-label="Back to main view"
        >
          <ChevronLeft size={20} />
          Back
        </button>
        
        <h1 className="lesson-flow-title">
          <BookOpen size={24} />
          Lesson Flow by Class
        </h1>
      </div>

      <div className="lesson-flow-controls">
        <div className="class-selector">
          <label htmlFor="class-select">Select Class:</label>
          <select 
            id="class-select"
            value={selectedClass || ''}
            onChange={(e) => setSelectedClass(e.target.value || null)}
            className="class-select-dropdown"
          >
            <option value="">Choose a class...</option>
            {classes.map(className => (
              <option key={className} value={className}>
                {className}
              </option>
            ))}
          </select>
        </div>

        <div className="date-range-selector">
          <label htmlFor="date-range">Show next:</label>
          <select 
            id="date-range"
            value={dateRange}
            onChange={(e) => setDateRange(Number(e.target.value))}
            className="date-range-dropdown"
          >
            <option value={3}>3 days</option>
            <option value={7}>1 week</option>
            <option value={14}>2 weeks</option>
            <option value={30}>1 month</option>
          </select>
        </div>
      </div>

      {selectedClass ? (
        <div className="lesson-flow-content">
          <div className="class-header">
            <h2 className="class-name">
              <Users size={20} />
              {selectedClass}
            </h2>
            <div className="class-stats">
              {upcomingEvents.length} lesson{upcomingEvents.length !== 1 ? 's' : ''} (including previous 2 + next {dateRange} day{dateRange !== 1 ? 's' : ''})
            </div>
          </div>

          {upcomingEvents.length > 0 ? (
            <div className="lessons-timeline">
              {Object.entries(groupedEvents)
                .sort(([a], [b]) => new Date(a) - new Date(b))
                .map(([date, dayEvents]) => (
                  <div key={date} className="day-group">
                    <div className="day-header">
                      <h3 className="day-date">
                        {format(parseISO(date), 'EEEE, MMMM d, yyyy')}
                      </h3>
                      <span className="day-count">
                        {dayEvents.length} lesson{dayEvents.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    
                    <div className="day-lessons">
                      {dayEvents.map((event, index) => {
                        const eventDate = new Date(event.date);
                        const today = new Date();
                        const isPreviousLesson = eventDate < today;
                        
                        return (
                          <div 
                            key={event.id}
                            className={`lesson-card ${index === 0 ? 'first-lesson' : ''} ${index === dayEvents.length - 1 ? 'last-lesson' : ''} ${isPreviousLesson ? 'previous-lesson' : ''}`}
                            onClick={() => onEventClick(event)}
                          >
                          <div className="lesson-time">
                            <Clock size={16} />
                            {event.start_time} - {event.end_time}
                          </div>
                          
                          <div className="lesson-details">
                            <h4 className="lesson-title">{event.title}</h4>
                            {event.room && (
                              <div className="lesson-room">
                                <MapPin size={14} />
                                {event.room}
                              </div>
                            )}
                            {event.description && (
                              <p className="lesson-description">{event.description}</p>
                            )}
                          </div>
                          
                          <div className="lesson-flow-indicator">
                            {index < dayEvents.length - 1 && (
                              <div className="flow-arrow">
                                <ChevronRight size={16} />
                              </div>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="no-lessons">
              <BookOpen size={48} />
              <h3>No lessons found</h3>
              <p>No lessons found for {selectedClass} (including previous 2 lessons and next {dateRange} days).</p>
            </div>
          )}
        </div>
      ) : (
        <div className="no-class-selected">
          <Users size={48} />
          <h3>Select a Class</h3>
          <p>Choose a class from the dropdown above to view the lesson flow.</p>
        </div>
      )}
    </div>
  );
};

export default LessonFlow;
